/**
 * CS: Size chart popup interactions.
 * Web Component wrapping #csSizeChartPop.
 * Handles open/close, unit toggles (IN/CM), and tab switching.
 * Data loading (sizeChartLoaded event) handled by inline script in cs-size-chart-pop.liquid.
 */
class CsSizeChart extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    /* Open button (external — in product form area) */
    const openBtn = document.querySelector('#csSizeChartButton');
    if (openBtn) {
      openBtn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          this.classList.add('active');
          document.body.classList.add('cs-disable-scroll');
        },
        { signal }
      );
    }

    /* Close button */
    const closeBtn = this.querySelector('#csSizeChartButtonClose');
    if (closeBtn) {
      closeBtn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          this.close();
        },
        { signal }
      );
    }

    /* Backdrop click */
    const container = this.querySelector('.cs-size-chart-container');
    if (container) {
      container.addEventListener(
        'click',
        (e) => {
          if (e.target === container) this.close();
        },
        { signal }
      );
    }

    /* Escape key */
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') this.close();
      },
      { signal }
    );

    /* Unit toggles — Product Measurements */
    this.setupToggle(
      '#csToggleCm',
      '#csToggleIn',
      '#sizeChartTableInches',
      '#sizeChartTableCentimeters',
      signal
    );

    /* Unit toggles — Body Measurements */
    this.setupToggle(
      '#csToggleCmBody',
      '#csToggleInBody',
      '#sizeChartTableInchesBody',
      '#sizeChartTableCentimetersBody',
      signal
    );

    /* Set default active tables */
    this.querySelector('#sizeChartTableInches')?.classList.add('active');
    this.querySelector('#sizeChartTableInchesBody')?.classList.add('active');

    /* Tab switching */
    this.setupTabs(signal);
  }

  close() {
    if (this.classList.contains('active')) {
      this.classList.remove('active');
      document.body.classList.remove('cs-disable-scroll');
    }
  }

  setupToggle(cmId, inId, inchesTableId, cmTableId, signal) {
    const inchesTable = this.querySelector(inchesTableId);
    const cmTable = this.querySelector(cmTableId);
    if (!inchesTable || !cmTable) return;

    const handler = (e) => {
      if (e.target.value === 'CM') {
        inchesTable.classList.remove('active');
        cmTable.classList.add('active');
      } else if (e.target.value === 'IN') {
        inchesTable.classList.add('active');
        cmTable.classList.remove('active');
      }
    };

    this.querySelector(cmId)?.addEventListener('change', handler, { signal });
    this.querySelector(inId)?.addEventListener('change', handler, { signal });
  }

  setupTabs(signal) {
    const tabBtn = this.querySelector('#csSizeChartShowButton');
    const tabBtnBody = this.querySelector('#csSizeChartShowButtonBody');
    const section = this.querySelector('#csSizeChart');
    const sectionBody = this.querySelector('#csSizeChartBody');
    if (!tabBtn || !tabBtnBody || !section || !sectionBody) return;

    tabBtn.addEventListener(
      'click',
      () => {
        section.classList.add('active');
        sectionBody.classList.remove('active');
        tabBtn.classList.add('active');
        tabBtnBody.classList.remove('active');
        tabBtn.setAttribute('aria-selected', 'true');
        tabBtnBody.setAttribute('aria-selected', 'false');
      },
      { signal }
    );

    tabBtnBody.addEventListener(
      'click',
      () => {
        section.classList.remove('active');
        sectionBody.classList.add('active');
        tabBtn.classList.remove('active');
        tabBtnBody.classList.add('active');
        tabBtnBody.setAttribute('aria-selected', 'true');
        tabBtn.setAttribute('aria-selected', 'false');
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}
customElements.define('cs-size-chart', CsSizeChart);
